import { clampEndDateToMaxRange } from "./sportsbookAnalyticsSummaryMapper";
import {
  buildSportsbookAnalyticsFilterParams,
  type SportsbookAnalyticsHttpParams,
} from "./sportsbookAnalyticsFilterParams";
import type {
  SportsbookKpisHttpBody,
  SportsbookKpisQueryParams,
  SportsbookKpisResult,
} from "./sportsbookAnalyticsKpisTypes";

function coerceNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/**
 * Parses the KPIs HTTP envelope into the normalized `SportsbookKpisResult`.
 * Missing / non-numeric fields collapse to `0` so the UI stays renderable.
 */
export function parseSportsbookKpisResponse(
  body: unknown,
): Omit<SportsbookKpisResult, "wasRangeClamped"> {
  const b = body as SportsbookKpisHttpBody | null | undefined;
  const data = b?.data;
  return {
    turnover: coerceNumber(data?.turnover),
    pnl: coerceNumber(data?.pnl),
    margin: coerceNumber(data?.margin),
  };
}

/**
 * Builds the axios `params` object for the KPIs request.
 *
 * - Clamps the date range so KPIs and the summary table stay aligned.
 * - Keeps money type/status repeatable; other filter groups remain comma-joined.
 * - Omits empty / undefined filter arrays so the backend applies its own defaults.
 */
export function buildSportsbookKpisParams(args: SportsbookKpisQueryParams): {
  params: SportsbookAnalyticsHttpParams;
  wasClamped: boolean;
} {
  const { start_date, end_date, wasClamped } = clampEndDateToMaxRange(
    args.start_date,
    args.end_date,
  );

  const params: SportsbookAnalyticsHttpParams = {
    start_date,
    end_date,
    ...buildSportsbookAnalyticsFilterParams(args),
  };

  return { params, wasClamped };
}
